import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, Search, BookOpen } from "lucide-react";
import MultiSelect from "../../components/MultiSelect";

const empty = {
  first_name: "", last_name: "", birth_date: "", address: "",
  parent_name: "", phone: "", email: "", note: "", active: true,
  course_ids: [],
  per_lesson_overrides: {},
  monthly_fee_overrides: {},
};

const Students = () => {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/students").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/courses").then(r => setCourses(r.data));
  }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (s) => { setForm({ ...empty, ...s, course_ids: s.course_ids || [], per_lesson_overrides: s.per_lesson_overrides || {}, monthly_fee_overrides: s.monthly_fee_overrides || {} }); setEditing(s); };
  const close = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, active: !!form.active };
      if (editing === "new") await api.post("/admin/students", payload);
      else await api.put(`/admin/students/${editing.id}`, payload);
      toast.success("Gespeichert");
      close();
      load();
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setLoading(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Schüler ${s.first_name} ${s.last_name} wirklich löschen?`)) return;
    await api.delete(`/admin/students/${s.id}`);
    toast.success("Gelöscht");
    load();
  };

  const filtered = items.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.first_name, s.last_name, s.email, s.phone, s.parent_name].some(v => (v || "").toLowerCase().includes(q));
  });

  const courseOptions = courses.map(c => ({
    value: c.id,
    label: c.name,
    sub: `${c.category === "music" ? "Musik" : "Tanz"} · ${c.weekday || ""} ${c.time || ""}`.trim(),
  }));

  const courseLabel = (id) => courses.find(c => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="admin-students">
      <Toaster position="top-center" theme="light" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl">Schüler</h1>
          <p className="text-sm text-muted-foreground">{items.length} Einträge</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              placeholder="Suchen…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="students-search"
              className="bg-input/30 border border-border rounded-md pl-9 pr-3 py-2 text-sm w-64"
            />
          </div>
          <button onClick={openNew} data-testid="students-new-btn" className="inline-flex items-center gap-2 btn-gold px-5 py-2.5 rounded-full text-sm">
            <Plus className="w-4 h-4" /> Neu
          </button>
        </div>
      </div>

      <div className="card-academy rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr>
                {["Schüler", "Geburtsdatum", "Kontakt", "Eltern", "Kurse", "Aktiv", "Aktion"].map(h =>
                  <th key={h} className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-4 py-3 font-medium">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.birth_date || "—"}</td>
                  <td className="px-4 py-3 text-xs"><div>{s.email}</div><div className="text-muted-foreground">{s.phone}</div></td>
                  <td className="px-4 py-3">{s.parent_name || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.course_ids || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}
                      {(s.course_ids || []).map(cid => (
                        <span key={cid} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5">
                          <BookOpen className="w-3 h-3" />{courseLabel(cid)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{s.active ? <span className="text-primary">Ja</span> : <span className="text-muted-foreground">Nein</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(s)} data-testid={`student-edit-${s.id}`} className="text-muted-foreground hover:text-primary mr-3"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(s)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Keine Schüler</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" role="dialog" data-testid="student-modal" onClick={close}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="font-serif-display text-xl">{editing === "new" ? "Neuer Schüler" : `${form.first_name} ${form.last_name}`}</div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Vorname" required value={form.first_name} onChange={v => setForm({ ...form, first_name: v })} />
                <Field label="Nachname" required value={form.last_name} onChange={v => setForm({ ...form, last_name: v })} />
                <Field label="Geburtsdatum" type="date" value={form.birth_date} onChange={v => setForm({ ...form, birth_date: v })} />
                <Field label="Telefon" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
                <Field label="E-Mail" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
                <Field label="Elternkontakt" value={form.parent_name} onChange={v => setForm({ ...form, parent_name: v })} />
                <Field label="Adresse" full value={form.address} onChange={v => setForm({ ...form, address: v })} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Zugewiesene Kurse</label>
                <MultiSelect
                  options={courseOptions}
                  value={form.course_ids}
                  onChange={(v) => setForm({ ...form, course_ids: v })}
                  placeholder="Kurse zuweisen (suchen…)"
                />
              </div>

              {form.course_ids.length > 0 && (
                <div className="border border-border rounded-md p-4 bg-secondary/20 space-y-3" data-testid="student-fee-overrides">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Individuelle Beträge pro Kurs</div>
                    <p className="text-xs text-muted-foreground mt-1">Leer lassen für Standard-Preis des Kurses. Monatsbetrag (flat) überschreibt die automatische Berechnung.</p>
                  </div>
                  {form.course_ids.map(cid => {
                    const course = courses.find(c => c.id === cid);
                    if (!course) return null;
                    const pl = form.per_lesson_overrides?.[cid] ?? "";
                    const mf = form.monthly_fee_overrides?.[cid] ?? "";
                    return (
                      <div key={cid} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center pb-3 border-b border-border/50 last:border-0 last:pb-0">
                        <div className="text-sm font-medium">{course.name}<div className="text-xs text-muted-foreground">Standard: CHF {course.price_per_lesson}/Lektion</div></div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Preis/Lektion (CHF)</label>
                          <input type="number" step="0.5" placeholder={`Standard ${course.price_per_lesson}`}
                            value={pl}
                            onChange={e => {
                              const v = e.target.value;
                              const nx = { ...(form.per_lesson_overrides || {}) };
                              if (v === "" || v == null) delete nx[cid]; else nx[cid] = parseFloat(v);
                              setForm({ ...form, per_lesson_overrides: nx });
                            }}
                            data-testid={`fee-per-lesson-${cid}`}
                            className="w-full bg-input/30 border border-border rounded-md px-3 py-1.5 text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Monatsbetrag flat (CHF)</label>
                          <input type="number" step="0.5" placeholder="auto-berechnet"
                            value={mf}
                            onChange={e => {
                              const v = e.target.value;
                              const nx = { ...(form.monthly_fee_overrides || {}) };
                              if (v === "" || v == null) delete nx[cid]; else nx[cid] = parseFloat(v);
                              setForm({ ...form, monthly_fee_overrides: nx });
                            }}
                            data-testid={`fee-monthly-${cid}`}
                            className="w-full bg-input/30 border border-border rounded-md px-3 py-1.5 text-sm" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Bemerkung</label>
                <textarea rows={3} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="accent-[hsl(35_90%_55%)]" />
                <span className="text-sm">Aktiv</span>
              </label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">Abbrechen</button>
              <button type="submit" disabled={loading} data-testid="student-save-btn" className="px-6 py-2 text-sm rounded-full btn-gold disabled:opacity-50">{loading ? "…" : "Speichern"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</label>
    <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} required={required}
      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
  </div>
);

export default Students;
